import { useCallback, useEffect, useState } from 'react';
import { X } from '@phosphor-icons/react';
import {
  useImportWizard,
  useCampaignCapabilitiesStore,
  type ImportBatchApi,
  type ImportProgress,
  type ImportWizardStep,
  IMPORT_STILL_RUNNING_MESSAGE,
} from '@discovr/features';
import type { ImportTarget, OutreachImportBatch } from '@discovr/types';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { cn } from '../../../lib/utils';
import { ImportUploadStep, PASTED_SHEET_NAME, type UploadedSheet } from './ImportUploadStep';
import { ImportMappingStep } from './ImportMappingStep';
import { HANDLES_ESCAPE_SELECTOR, ImportReviewStep, type ImportingProgress } from './ImportReviewStep';
import { ImportDoneStep, ImportDoneWithoutResult } from './ImportDoneStep';
import { ImportStepper } from './ImportStepper';
import { ImportWizardFooterProvider } from './ImportWizardFooterSlot';
import { ImportResultsPanel } from './ImportResultsPanel';
import { driftKeptHandles, ignoreSurfacedError, importDoneTally, outreachSettledCount } from './importWizardHelpers';
import { countAlreadyInCampaign } from './reviewRowStatus';
import { importWizardSubtitle, type ImportWizardSubtitle } from './importWizardSubtitle';
import { useReturnFocusToOpener } from './useReturnFocusToOpener';

type ImportWizard = ReturnType<typeof useImportWizard>;
type DialogSize = 'default' | 'wide';

const STEP_TITLES: Record<ImportWizardStep, string> = {
  upload: 'Upload creator sheet',
  mapping: 'Match your columns',
  review: 'Review your sheet',
  importing: 'Review your sheet',
  done: 'Creators imported',
};

const OUTREACH_STEP_TITLES: Record<ImportWizardStep, string> = {
  upload: 'Add creators',
  mapping: 'Match your columns',
  review: 'Review creators',
  importing: 'Review creators',
  done: 'Import complete',
};

const STEP_TITLES_BY_TARGET: Record<ImportTarget, Record<ImportWizardStep, string>> = {
  pipeline: STEP_TITLES,
  outreach: OUTREACH_STEP_TITLES,
};

const OUTREACH_TARGET: ImportTarget = 'outreach';
const OUTREACH_FAILED_STATUS: OutreachImportBatch['status'] = 'failed';

const REVIEW_STEP: ImportWizardStep = 'review';
// Review and importing both show the grid, which renders its own error banner.
const GRID_STEPS: ReadonlySet<ImportWizardStep> = new Set<ImportWizardStep>(['review', 'importing']);
const IMPORTING_STEP: ImportWizardStep = 'importing';
const DONE_STEP: ImportWizardStep = 'done';
const SCREEN_READER_ONLY_CLASS = 'sr-only';

const DIALOG_BASE_CLASS =
  'flex max-w-[calc(100vw-2rem)] sm:max-w-[calc(100vw-2rem)] flex-col gap-0 overflow-hidden rounded-2xl p-0';
const DIALOG_SIZE_CLASS: Record<DialogSize, string> = {
  default: 'h-[600px] max-h-[calc(100vh-4rem)] w-3xl',
  wide: 'h-[calc(100vh-7rem)] max-h-[900px] w-[1100px]',
};
const ERROR_BANNER_CLASS =
  'mb-4 rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-3 text-body-sm-regular text-destructive';

const RESUMABLE_TITLE = 'You have an import in progress';
const BLOCKING_TITLE = 'An import is already in progress for this campaign';
const OTHER_TAB_NAME: Record<ImportTarget, string> = { pipeline: 'Creators', outreach: 'Outreach' };
const DEFAULT_RESUME_STEP_TITLE = STEP_TITLES.review;

/** Nothing to fall back to before the campaign header query resolves. */
const NO_PLATFORM = '';

interface CreatorImportWizardProps {
  open: boolean;
  campaignId: string;
  api: ImportBatchApi;
  /** Shown on the done screen: "N creators are in ‹campaign›". */
  campaignName?: string;
  payoutModel?: string;
  campaignPlatform?: string;
  /** A finished import's roster wasn't refreshed by the live import event (the
   *  status check found the result instead), so the page should reload it. */
  onRosterStale?: () => void;
  /** The admin left the done screen. `added` is how many new creators the
   *  import put on the campaign, so the page can show where they landed. */
  onImported?: (added: number) => void;
  /** Outreach only: the results panel's "Send first touch to all", with the
   *  roster entry ids the import created or matched. Omit to hide it. */
  onSendFirstTouch?: (entryIds: string[]) => void;
  onClose: () => void;
}

// One size from upload through importing, so the modal doesn't resize between
// steps; only the done screen is the smaller one.
function dialogSize(step: ImportWizardStep): DialogSize {
  return step === DONE_STEP ? 'default' : 'wide';
}

/** Always the dialog's accessible description; shown only on the steps that
 *  have a subtitle. */
function StepSubtitle({ subtitle }: { subtitle: ImportWizardSubtitle }) {
  return (
    <DialogDescription className={cn(!subtitle.visible && SCREEN_READER_ONLY_CLASS)}>{subtitle.text}</DialogDescription>
  );
}

// The conflict card and the review grid each show `wizard.error` themselves,
// so the top banner stays out of their way to avoid showing it twice.
function shouldShowWizardError(wizard: ImportWizard): boolean {
  if (wizard.error === null) return false;
  if (wizard.activeConflictImportId !== null) return false;
  return !GRID_STEPS.has(wizard.step);
}

function ErrorBanner({ message }: { message: string | null }) {
  if (message === null) return null;
  return (
    <div role="alert" className={ERROR_BANNER_CLASS}>
      {message}
    </div>
  );
}

/** Commit progress for the grid's footer: the live SSE count when one has
 *  arrived, otherwise nothing done yet out of the commit's own total. */
function importingProgress(progress: ImportProgress | null, importTotal: number): ImportingProgress {
  if (progress === null) return { done: 0, total: importTotal };
  return { done: progress.done, total: progress.total };
}

/** An outreach import's progress comes from its outreach batch, which counts
 *  every settled item, created, matched, skipped or failed. */
function outreachProgress(batch: OutreachImportBatch | null, importTotal: number): ImportingProgress {
  if (batch === null) return { done: 0, total: importTotal };
  return { done: outreachSettledCount(batch), total: batch.rowCount };
}

function wizardImportingProgress(wizard: ImportWizard, sharedProgress: ImportProgress | null): ImportingProgress | null {
  if (wizard.step !== IMPORTING_STEP) return null;
  if (wizard.target === OUTREACH_TARGET) return outreachProgress(wizard.outreachBatch, wizard.importTotal);
  return importingProgress(sharedProgress, wizard.importTotal);
}

/** Whether the review grid is being edited — so Esc and outside clicks, which
 *  Radix handles before the grid sees them, don't close the dialog mid-edit.
 *  ImportReviewStep unmounting mid-edit never reports the end of editing, so
 *  the flag is also dropped whenever the wizard leaves the review step. */
function useReviewEditingLock(step: ImportWizardStep) {
  const [editing, setEditing] = useState(false);
  const [trackedStep, setTrackedStep] = useState(step);
  if (trackedStep !== step) {
    setTrackedStep(step);
    if (step !== REVIEW_STEP) setEditing(false);
  }
  const clear = useCallback(() => setEditing(false), []);
  return { locked: editing && step === REVIEW_STEP, setEditing, clear };
}

/** New creators a finished pipeline import put on the campaign; they land as
 *  pending, so this is what decides whether to point the admin there. */
function addedCreators(wizard: ImportWizard): number {
  if (wizard.target === OUTREACH_TARGET || wizard.doneResult === null) return 0;
  return importDoneTally(wizard.rows, wizard.doneResult, wizard.deflectedRows).added;
}

function ResumeDetails({ wizard }: { wizard: ImportWizard }) {
  const batch = wizard.resumableBatch;
  if (wizard.blockedByTarget !== null) {
    return (
      <>
        An import started from the {OTHER_TAB_NAME[wizard.blockedByTarget]} tab is still in progress. Finish or discard it
        there before starting another here.
      </>
    );
  }
  if (batch === null) {
    return (
      <>
        This could be another admin uploading right now, another tab, or an import left over from a closed session.
        Discard it to start a fresh upload — anything already reviewed in it will be lost.
      </>
    );
  }
  const stepTitle = wizard.resumableStep ? STEP_TITLES[wizard.resumableStep] : DEFAULT_RESUME_STEP_TITLE;
  return (
    <>
      <span className="font-medium text-foreground">{batch.fileName}</span>
      {batch.uploadedByName && (
        <>
          {' '}— started by <span className="font-medium text-foreground">{batch.uploadedByName}</span>
        </>
      )}
      {' '}— still at {stepTitle}. Pick up where you left off, or discard it and start fresh.
    </>
  );
}

/** Its own failures — a discard that didn't go through, or a change of file
 *  that left the old batch active — show here, next to the buttons to retry.
 *  The upload 409 that raises the card leaves no error: the card says it. */
function ActiveImportCard({ wizard }: { wizard: ImportWizard }) {
  const resumable = wizard.resumableBatch !== null;
  const otherTab = wizard.blockedByTarget !== null;
  return (
    <div className="flex flex-col items-center gap-3 py-6 text-center">
      <ErrorBanner message={wizard.error} />
      <p className="text-body-sm-semibold text-foreground">{resumable ? RESUMABLE_TITLE : BLOCKING_TITLE}</p>
      <p className="text-caption-regular text-muted-foreground">
        <ResumeDetails wizard={wizard} />
      </p>
      <div className="flex gap-2">
        {resumable && (
          <Button variant="default" size="sm" type="button" disabled={wizard.busy} onClick={wizard.resumeActiveImport}>
            Resume import
          </Button>
        )}
        {!otherTab && (
          <Button
            variant="outline"
            size="sm"
            type="button"
            disabled={wizard.busy}
            onClick={() => {
              wizard.discardActiveConflict().catch(ignoreSurfacedError);
            }}
          >
            Discard and try again
          </Button>
        )}
      </div>
    </div>
  );
}

function CheckingSpinner() {
  return (
    <div className="flex items-center justify-center py-12">
      <span className="size-5 animate-spin rounded-full border-2 border-border border-t-foreground" />
    </div>
  );
}

/** Back from mapping keeps the sheet on the server, so Upload shows it as a
 *  file card; removing it discards that batch. */
function uploadedSheet(wizard: ImportWizard): UploadedSheet | null {
  if (wizard.importId === null) return null;
  return {
    fileName: wizard.fileName ?? '',
    rowCount: wizard.rowCount,
    onContinue: wizard.continueToMapping,
    onRemove: wizard.changeFile,
  };
}

interface StepBodyProps {
  wizard: ImportWizard;
  payoutModel?: string;
  campaignPlatform: string;
}

function UploadStepBody({ wizard, payoutModel, campaignPlatform }: StepBodyProps) {
  if (wizard.checkingActive) return <CheckingSpinner />;
  if (wizard.activeConflictImportId !== null) return <ActiveImportCard wizard={wizard} />;
  return (
    <ImportUploadStep
      target={wizard.target}
      payoutModel={payoutModel}
      campaignPlatform={campaignPlatform}
      expectedFields={wizard.expectedFields}
      busy={wizard.busy}
      onUpload={wizard.uploadFile}
      uploaded={uploadedSheet(wizard)}
    />
  );
}

interface ReviewStepBodyProps extends StepBodyProps {
  importing: ImportingProgress | null;
  onEditingChange: (editing: boolean) => void;
  onDiscard: () => Promise<void>;
}

function ReviewStepBody({ wizard, payoutModel, campaignPlatform, importing, onEditingChange, onDiscard }: ReviewStepBodyProps) {
  return (
    <ImportReviewStep
      target={wizard.target}
      rows={wizard.rows}
      expectedFields={wizard.expectedFields}
      mapping={wizard.mapping}
      campaignPlatform={campaignPlatform}
      payoutModel={payoutModel}
      busy={wizard.busy}
      error={wizard.error}
      hasSavedChanges={wizard.hasSavedChanges}
      lastChange={wizard.lastChange}
      driftRowNumbers={wizard.driftRowNumbers}
      onSaveRows={wizard.saveRows}
      onUndo={wizard.undoLastChange}
      onDismissChange={wizard.dismissLastChange}
      onBack={wizard.backToMapping}
      onImport={wizard.commit}
      onDiscard={onDiscard}
      notice={wizard.followingRunningImport ? IMPORT_STILL_RUNNING_MESSAGE : null}
      engagementReadAsFraction={wizard.engagementReadAsFraction}
      onEditingChange={onEditingChange}
      importing={importing}
      rowNumberOffset={wizard.fileName === PASTED_SHEET_NAME ? 1 : 0}
    />
  );
}

interface DoneStepBodyProps {
  wizard: ImportWizard;
  campaignName: string;
  onSendFirstTouch?: (entryIds: string[]) => void;
  onDone: () => void;
}

interface OutreachDoneStepBodyProps extends Pick<DoneStepBodyProps, 'onSendFirstTouch' | 'onDone'> {
  batch: OutreachImportBatch;
  /** Rows the review skipped as already in the campaign, which never reached
   *  the outreach batch and so aren't in its counts. The server's own count
   *  when the commit gave one, else counted from the review rows. */
  alreadyInCampaign: number;
  onRetryFailed: () => void;
  retrying: boolean;
}

function OutreachDoneStepBody({
  batch,
  alreadyInCampaign,
  onSendFirstTouch,
  onRetryFailed,
  retrying,
  onDone,
}: OutreachDoneStepBodyProps) {
  return (
    <ImportResultsPanel
      mode={OUTREACH_TARGET}
      outreachResult={{
        created: batch.createdCount,
        matched: batch.matchedCount + alreadyInCampaign,
        skipped: batch.skippedCount,
        failed: batch.failedCount,
        createdEntryIds: batch.createdEntryIds,
      }}
      onSendFirstTouch={onSendFirstTouch}
      outreachRetry={{ onRetry: onRetryFailed, retrying }}
      onReset={onDone}
    />
  );
}

// Reads the result the hook captured at markDone, not the shared store's,
// which a later import event may clear while this screen is still open.
function DoneStepBody({ wizard, campaignName, onSendFirstTouch, onDone }: DoneStepBodyProps) {
  if (wizard.target === OUTREACH_TARGET) {
    if (wizard.outreachBatch === null) return <ImportDoneWithoutResult onDone={onDone} />;
    // A failed import's reason is in the wizard's error banner; there is no
    // complete set to send a first touch to.
    const failed = wizard.outreachBatch.status === OUTREACH_FAILED_STATUS;
    return (
      <OutreachDoneStepBody
        batch={wizard.outreachBatch}
        alreadyInCampaign={wizard.outreachSkippedExisting ?? countAlreadyInCampaign(wizard.rows)}
        onSendFirstTouch={failed ? undefined : onSendFirstTouch}
        onRetryFailed={() => void wizard.retryFailedOutreachImport()}
        retrying={wizard.busy}
        onDone={onDone}
      />
    );
  }
  const result = wizard.doneResult;
  if (result === null) return <ImportDoneWithoutResult onDone={onDone} />;
  // `wizard.rows` is still the snapshot from just before the commit;
  // `wizard.deflectedRows` arrives shortly after done (read from the batch).
  return (
    <ImportDoneStep
      result={result}
      tally={importDoneTally(wizard.rows, result, wizard.deflectedRows)}
      driftKeptHandles={driftKeptHandles(wizard.rows, wizard.deflectedRows)}
      driftKeptUnconfirmed={wizard.deflectedRowsUnconfirmed}
      campaignName={campaignName}
      onDone={onDone}
    />
  );
}

export function CreatorImportWizard({
  open,
  campaignId,
  api,
  campaignName = '',
  payoutModel,
  campaignPlatform = NO_PLATFORM,
  onSendFirstTouch,
  onRosterStale,
  onImported,
  onClose,
}: CreatorImportWizardProps) {
  const wizard = useImportWizard(campaignId, api, { onRosterStale });
  const { target, busy } = wizard;
  const editingLock = useReviewEditingLock(wizard.step);
  const focusReturn = useReturnFocusToOpener();

  const importProgress = useCampaignCapabilitiesStore((s) => s.importProgress);
  const importResult = useCampaignCapabilitiesStore((s) => s.importResult);

  // Pipeline only: an outreach import moves itself to done from its own batch.
  const isOutreach = target === OUTREACH_TARGET;
  useEffect(() => {
    if (!isOutreach && wizard.step === IMPORTING_STEP && importResult) wizard.markDone(importResult);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `wizard` (the whole object) is a new reference every render; depending on it would re-run this effect every render instead of only on the step/result transitions it actually cares about. `wizard.markDone` is itself a stable useCallback identity.
  }, [isOutreach, wizard.step, importResult, wizard.markDone]);

  // Checks for an active import (and fetches expected columns) the instant
  // the wizard opens — before any file is chosen — so a campaign that's
  // already mid-import shows the "Discard and try again" card immediately.
  // checkStatus never rejects: it reports its own failure in wizard.error (the
  // server-side guard on upload still applies).
  useEffect(() => {
    if (open) void wizard.checkStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only re-check on open/close or a campaign switch, not every render (see the effect above for why `wizard` itself isn't a dep).
  }, [open, campaignId]);

  // Leaving the done screen any way (Done, X, Esc, outside click) points the
  // page at where the new creators landed.
  const reportImported = () => {
    const added = wizard.step === DONE_STEP ? addedCreators(wizard) : 0;
    if (added > 0) onImported?.(added);
  };

  const handleClose = () => {
    editingLock.clear();
    reportImported();
    void wizard.close();
    onClose();
  };

  const handleDone = () => {
    editingLock.clear();
    reportImported();
    wizard.reset();
    onClose();
  };

  // False when the import turned out to be running already: it is followed, not closed.
  const handleDiscard = async () => {
    if (!(await wizard.discardImport())) return;
    editingLock.clear();
    onClose();
  };

  const handleOpenChange = (next: boolean) => {
    if (next || busy) return;
    handleClose();
  };

  const blockWhileEditing = (event: { preventDefault: () => void }) => {
    if (editingLock.locked) event.preventDefault();
  };

  // Radix sees Escape on the document before the Back confirmation's own
  // handler, so without this Escape would close (and discard) the import.
  const handleEscapeKeyDown = (event: KeyboardEvent) => {
    if (event.target instanceof Element && event.target.closest(HANDLES_ESCAPE_SELECTOR)) {
      event.preventDefault();
      return;
    }
    blockWhileEditing(event);
  };

  const size = dialogSize(wizard.step);
  const subtitle = importWizardSubtitle(wizard.step, { name: campaignName, platform: campaignPlatform }, target);
  const stepBody = { wizard, payoutModel, campaignPlatform };
  const importing = wizardImportingProgress(wizard, importProgress);

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        showCloseButton={false}
        data-size={size}
        onEscapeKeyDown={handleEscapeKeyDown}
        onInteractOutside={blockWhileEditing}
        onOpenAutoFocus={focusReturn.onOpenAutoFocus}
        onCloseAutoFocus={focusReturn.onCloseAutoFocus}
        className={cn(DIALOG_BASE_CLASS, DIALOG_SIZE_CLASS[size])}
      >
        <div className="flex shrink-0 flex-col gap-3 border-b border-border px-6 pt-4 pb-3">
          <div className="flex flex-col gap-1">
            <div className="flex items-center justify-between gap-3">
              <DialogTitle className="text-heading-h7 text-foreground">{STEP_TITLES_BY_TARGET[target][wizard.step]}</DialogTitle>
              <Button
                variant="ghost"
                size="icon-sm"
                type="button"
                onClick={handleClose}
                disabled={busy}
                aria-label="Close"
                className="-mr-2 text-muted-foreground"
              >
                <X size={18} />
              </Button>
            </div>
            <StepSubtitle subtitle={subtitle} />
          </div>
          <ImportStepper step={wizard.step} />
        </div>

        <ImportWizardFooterProvider>
          <div className="min-h-0 flex-1 overflow-y-auto px-6 pt-2 pb-5">
            {shouldShowWizardError(wizard) && <ErrorBanner message={wizard.error} />}
            {wizard.step === 'upload' && <UploadStepBody {...stepBody} />}
            {wizard.step === 'mapping' && (
              <ImportMappingStep
                sheetColumns={wizard.sheetColumns}
                expectedFields={wizard.expectedFields}
                campaignPlatform={campaignPlatform}
                rowCount={wizard.rowCount}
                warnings={wizard.sheetWarnings}
                mapping={wizard.mapping}
                busy={wizard.busy}
                onBack={wizard.backToUpload}
                onSubmit={wizard.submitMapping}
              />
            )}
            {GRID_STEPS.has(wizard.step) && (
              <ReviewStepBody {...stepBody} importing={importing} onEditingChange={editingLock.setEditing} onDiscard={handleDiscard} />
            )}
            {wizard.step === 'done' && (
              <DoneStepBody wizard={wizard} campaignName={campaignName} onSendFirstTouch={onSendFirstTouch} onDone={handleDone} />
            )}
          </div>
        </ImportWizardFooterProvider>
      </DialogContent>
    </Dialog>
  );
}
